var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import React, { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Route, useHistory, useLocation } from 'react-router-dom';
import { Button, Headline, Layout, MultiColumnList, Pane, Paneset, Select, } from '@folio/stripes/components';
import { useErrorReportList } from '../hooks/useErrorReports';
import { TableLoadingSkeleton, TableRefetchOverlay } from '../components/TableStates';
import AuthorityTypeLabel from '../components/AuthorityTypeCard/AuthorityTypeLabel';
import ErrorReportDetailPane, { ERROR_REPORT_STATUS_LABEL } from '../components/ErrorReportDetail/ErrorReportDetailPane';
import { ERROR_REPORT_STATUSES } from '../types/errorReports';
var PAGE_SIZE = 50;
/** The status filter's "every status" value; absent from the URL means the default, New. */
var ALL = 'ALL';
var DEFAULT_STATUS = 'NEW';
var httpStatus = function (error) { var _a; return (_a = error === null || error === void 0 ? void 0 : error.response) === null || _a === void 0 ? void 0 : _a.status; };
/** `status` from the URL: a known status, ALL, or — anything else — the default. */
var readStatus = function (raw) {
    if (raw === ALL)
        return ALL;
    return ERROR_REPORT_STATUSES.includes(raw)
        ? raw
        : DEFAULT_STATUS;
};
var ErrorReportsScreen = function (_a) {
    var _b, _c, _d;
    var basePath = _a.basePath, reportId = _a.reportId;
    var intl = useIntl();
    var history = useHistory();
    var location = useLocation();
    var search = new URLSearchParams(location.search);
    var status = readStatus(search.get('status'));
    var recordId = (_b = search.get('recordId')) !== null && _b !== void 0 ? _b : undefined;
    var _f = useState(0), offset = _f[0], setOffset = _f[1];
    // A new filter starts at the first page.
    useEffect(function () { return setOffset(0); }, [status, recordId]);
    var list = useErrorReportList({
        status: status === ALL ? undefined : status,
        recordId: recordId,
        offset: offset,
        limit: PAGE_SIZE,
    });
    var go = function (path, params) {
        var query = params.toString();
        history.push(query ? "".concat(path, "?").concat(query) : path);
    };
    var setStatus = function (value) {
        var params = new URLSearchParams(location.search);
        params.set('status', value);
        go(location.pathname, params);
    };
    var clearRecord = function () {
        var params = new URLSearchParams(location.search);
        params.delete('recordId');
        go(location.pathname, params);
    };
    var filterByRecord = function (id) {
        var params = new URLSearchParams(location.search);
        params.set('status', ALL);
        params.set('recordId', id);
        go(location.pathname, params);
    };
    var openReport = function (_e, row) {
        go("".concat(basePath, "/").concat(row.id), new URLSearchParams(location.search));
    };
    var closeReport = function () { return go(basePath, new URLSearchParams(location.search)); };
    var statusLabel = function (value) {
        return intl.formatMessage({ id: ERROR_REPORT_STATUS_LABEL[value] });
    };
    var columnMapping = useMemo(function () { return ({
        createdAt: React.createElement(FormattedMessage, { id: "ui-czna-authorities.errorReports.column.createdAt" }),
        recordHeading: React.createElement(FormattedMessage, { id: "ui-czna-authorities.errorReports.column.recordHeading" }),
        recordAuthorityId: React.createElement(FormattedMessage, { id: "ui-czna-authorities.errorReports.column.record" }),
        authorityType: React.createElement(FormattedMessage, { id: "ui-czna-authorities.errorReports.column.authorityType" }),
        status: React.createElement(FormattedMessage, { id: "ui-czna-authorities.errorReports.column.status" }),
    }); }, []);
    var formatter = {
        createdAt: function (row) { return "".concat(intl.formatDate(row.createdAt), " ").concat(intl.formatTime(row.createdAt)); },
        recordHeading: function (row) { var _a; return (_a = row.recordHeading) !== null && _a !== void 0 ? _a : '—'; },
        // A report about a record the database does not know (stale public id): every NK
        // supervisor and the administrator handle it, and it says so instead of an empty cell.
        recordAuthorityId: function (row) {
            var _a;
            return (_a = row.recordAuthorityId) !== null && _a !== void 0 ? _a : intl.formatMessage({ id: 'ui-czna-authorities.errorReports.detail.recordMissing' });
        },
        authorityType: function (row) { return (row.authorityType
            ? React.createElement(AuthorityTypeLabel, { type: row.authorityType })
            : '—'); },
        status: function (row) { return statusLabel(row.status); },
    };
    var recordFilterLabel = recordId
        ? (_d = (_c = list.items.find(function (item) { return item.recordId === recordId; })) === null || _c === void 0 ? void 0 : _c.recordAuthorityId) !== null && _d !== void 0 ? _d : recordId
        : undefined;
    var renderMessage = function (id) { return (React.createElement(Layout, { className: "display-flex flex-direction-column centerContent" },
        React.createElement(Layout, { className: "padding-all-gutter" },
            React.createElement(Headline, { size: "medium", margin: "none" },
                React.createElement(FormattedMessage, { id: id }))))); };
    var renderList = function () {
        if (list.isError) {
            return renderMessage(httpStatus(list.error) === 403
                ? 'ui-czna-authorities.errorReports.list.forbidden'
                : 'ui-czna-authorities.errorReports.list.loadError');
        }
        if (list.isLoading && list.items.length === 0) {
            return React.createElement(TableLoadingSkeleton, { testId: "error-reports-loading", columns: [3, 5, 2, 2, 2], rows: 6 });
        }
        if (list.items.length === 0) {
            return renderMessage('ui-czna-authorities.errorReports.list.empty');
        }
        return (React.createElement(TableRefetchOverlay, { active: list.isFetching, testId: "error-reports-refetching" },
            React.createElement(MultiColumnList, { autosize: true, contentData: list.items, totalCount: list.totalRecords, pagingType: "prev-next", pagingOffset: offset, pageAmount: PAGE_SIZE, columnMapping: columnMapping, visibleColumns: ['createdAt', 'recordHeading', 'recordAuthorityId', 'authorityType', 'status'], formatter: formatter, onRowClick: openReport, onNeedMoreData: function (_amount, nextOffset) { return setOffset(nextOffset); }, selectedRow: reportId ? { id: reportId } : undefined })));
    };
    return (React.createElement(Paneset, null,
        React.createElement(Pane, { defaultWidth: "20%", paneTitle: React.createElement(FormattedMessage, { id: "ui-czna-authorities.errorReports.filter.title" }) },
            React.createElement("div", { "data-testid": "error-reports-filters" },
                React.createElement(Select, { label: intl.formatMessage({ id: 'ui-czna-authorities.errorReports.filter.status' }), value: status, onChange: function (e) { return setStatus(e.target.value); }, dataOptions: __spreadArray(__spreadArray([], ERROR_REPORT_STATUSES.map(function (value) { return ({ value: value, label: statusLabel(value) }); }), true), [
                        { value: ALL, label: intl.formatMessage({ id: 'ui-czna-authorities.errorReports.filter.all' }) },
                    ], false) }),
                recordId && (React.createElement("div", { "data-testid": "error-reports-record-filter", style: { marginTop: '12px' } },
                    React.createElement("div", { style: { fontWeight: 600, marginBottom: '4px' } },
                        React.createElement(FormattedMessage, { id: "ui-czna-authorities.errorReports.filter.record" })),
                    React.createElement("span", null, recordFilterLabel),
                    ' ',
                    React.createElement(Button, { buttonStyle: "link", marginBottom0: true, onClick: clearRecord },
                        React.createElement(FormattedMessage, { id: "ui-czna-authorities.errorReports.filter.recordClear" })))))),
        React.createElement(Pane, { defaultWidth: "fill", paneTitle: React.createElement(FormattedMessage, { id: "ui-czna-authorities.nav.errorReports" }), paneSub: list.totalRecords > 0
                ? intl.formatMessage({ id: 'ui-czna-authorities.errorReports.list.subtitle' }, { count: list.totalRecords })
                : undefined }, renderList()),
        reportId && (React.createElement(ErrorReportDetailPane, { id: reportId, onClose: closeReport, onFilterByRecord: filterByRecord }))));
};
/**
 * CZNA-1596 (UC PC-08) — "Hlášení od veřejnosti": the public error reports about records of
 * the types the signed-in user supervises nationally, with the selected report beside them.
 * New reports by default (NK, 30. 10. 2026); the filter lives in the URL, so the record-detail
 * banner can link straight to one record's reports.
 */
var ErrorReports = function (_a) {
    var match = _a.match;
    return (React.createElement(Route, { path: ["".concat(match.path, "/:id"), match.path], render: function (routeProps) { return (React.createElement(ErrorReportsScreen, { basePath: match.url, reportId: routeProps.match.params.id })); } }));
};
export default ErrorReports;
